import Phaser from 'phaser'
import type { GridView } from '../model/coords'
import { worldFromLogical } from '../model/coords'
import {
  DEFAULT_AIR_ROUTE_ID,
  DEFAULT_GROUND_ROUTE_ID,
  normalizeMapRoutes,
  type RouteDefinition
} from '../model/path'
import type { GameSession } from '../model/session'
import type { GridPosition, MapDefinition, WaveDefinition } from '../model/types'
import { alignPx, strokeWidthForCell } from './scale'
import { THEME } from './theme'
import { terrainPaletteForBiome } from './biomeTerrain'

export interface PathNeighbors {
  n: boolean
  e: boolean
  s: boolean
  w: boolean
}

/** Bitmask: N=1 E=2 S=4 W=8 */
export function pathNeighborMask(
  isPath: (x: number, y: number) => boolean,
  x: number,
  y: number
): number {
  let mask = 0
  if (isPath(x, y - 1)) mask |= 1
  if (isPath(x + 1, y)) mask |= 2
  if (isPath(x, y + 1)) mask |= 4
  if (isPath(x - 1, y)) mask |= 8
  return mask
}

export function neighborsFromMask(mask: number): PathNeighbors {
  return {
    n: (mask & 1) !== 0,
    e: (mask & 2) !== 0,
    s: (mask & 4) !== 0,
    w: (mask & 8) !== 0
  }
}

/** Fine cell hash 0..2 — kept for tests / micro variation. */
export function grassToneIndex(x: number, y: number): number {
  return Math.abs((x * 31 + y * 17) ^ (x * 7)) % 3
}

/** Large-scale patch tone 0..2 from map id + block coords (deterministic). */
export function grassMacroTone(mapId: string, x: number, y: number): number {
  let h = 2166136261
  for (let i = 0; i < mapId.length; i += 1) {
    h ^= mapId.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const bx = Math.floor(x / 5)
  const by = Math.floor(y / 5)
  h ^= bx * 73856093
  h ^= by * 19349663
  return Math.abs(h) % 3
}

/** Soft secondary variation inside a macro patch. */
export function grassMicroField(x: number, y: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
  return n - Math.floor(n)
}

/** Map identity motif — geometry only (not biome palettes). */
export type MapIdentityMotif = 'ridge' | 'dots' | 'arcs'

export function mapIdentityMotif(mapId: string): MapIdentityMotif {
  let h = 2166136261
  for (let i = 0; i < mapId.length; i += 1) {
    h ^= mapId.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const motifs: MapIdentityMotif[] = ['ridge', 'dots', 'arcs']
  return motifs[Math.abs(h) % 3]!
}

/** Compact lane mark matching wave intel (A/B/C/AIR). */
export function routeLaneMark(routeId: string): string {
  if (routeId === DEFAULT_AIR_ROUTE_ID || routeId.startsWith('air')) return 'AIR'
  if (routeId === 'ground-a' || routeId === DEFAULT_GROUND_ROUTE_ID) return 'A'
  if (routeId === 'ground-b') return 'B'
  if (routeId === 'ground-c') return 'C'
  const m = /^ground-([a-z])$/i.exec(routeId)
  if (m) return m[1]!.toUpperCase()
  return routeId
}

/** Spawn silhouette keyed by lane — shape first, letter reinforces. */
export type SpawnLaneShape = 'circle' | 'square' | 'hex'

export function spawnLaneShape(routeId: string): SpawnLaneShape {
  const mark = routeLaneMark(routeId)
  if (mark === 'B') return 'square'
  if (mark === 'C') return 'hex'
  return 'circle'
}

function defaultRouteForEnemy(enemyId: string): string {
  // Campaign V3: omitted routeId → physical primary ground path (AIR included).
  void enemyId
  return DEFAULT_GROUND_ROUTE_ID
}

/**
 * Route ids referenced by map waves (M73 stage-active policy when waves are
 * stage-baked). Presentation only — does not change simulation.
 */
export function activeRouteIdsFromMapWaves(
  waves: WaveDefinition[] | undefined
): string[] {
  const ids = new Set<string>()
  for (const wave of waves ?? []) {
    for (const group of wave.groups) {
      if (group.routeId) ids.add(group.routeId)
      else ids.add(defaultRouteForEnemy(group.enemy))
    }
  }
  return [...ids].sort()
}

function cellKey(x: number, y: number): string {
  return `${x},${y}`
}

/** Cells belonging to a route set (ground only for path-tile ownership). */
export function routeCellKeys(routes: RouteDefinition[]): Set<string> {
  const keys = new Set<string>()
  for (const route of routes) {
    for (const c of route.cells) keys.add(cellKey(c.x, c.y))
  }
  return keys
}

/** M80 terrain — close-toned macro patches (avoid loud checkerboard). */
const DEFAULT_PALETTE = terrainPaletteForBiome('grasslands')

export interface BoardDrawOptions {
  placing: boolean
  /**
   * Routes used by this session's waves. Omit → derive from map.waves.
   * Unused map lanes stay as shared path geometry but markers/overlays subdue.
   */
  activeRouteIds?: string[]
  /**
   * Session phase for AIR readability: READY (and pre-start) emphasizes the
   * flight corridor; PLAYING quiets it so combat stays readable.
   */
  sessionState?: 'READY' | 'PLAYING' | 'VICTORY' | 'DEFEAT' | string
  /** Campaign V3 biome terrain palette (grasslands default). */
  biomeId?: string
}

/**
 * Draw static board into Phaser Graphics containers.
 * One pass — not one GameObject per cell.
 */
export function paintBoardTerrain(
  graphics: Phaser.GameObjects.Graphics,
  gridGfx: Phaser.GameObjects.Graphics,
  edgeGfx: Phaser.GameObjects.Graphics,
  markerGfx: Phaser.GameObjects.Graphics,
  session: GameSession,
  definition: MapDefinition,
  view: GridView,
  options: BoardDrawOptions
): void {
  graphics.clear()
  gridGfx.clear()
  edgeGfx.clear()
  markerGfx.clear()

  const cell = view.cellSize
  const grid = session.grid
  const mapId = definition.id
  const inset = Math.max(0.5, cell * 0.02)
  const pathPad = Math.max(1, cell * 0.1)
  const placing = options.placing
  const palette = terrainPaletteForBiome(options.biomeId) ?? DEFAULT_PALETTE
  const GRASS_BASE = palette.grassBase
  const GRASS_SOFT = palette.grassSoft
  const GRASS_PLACE = palette.grassPlace
  const PATH_SUBDUED = palette.pathSubdued
  const PATH_SUBDUED_EDGE = palette.pathSubduedEdge

  const routes = normalizeMapRoutes(definition)
  const groundRoutes = routes.filter((r) => r.movementClass === 'GROUND')
  const airRoutes = routes.filter((r) => r.movementClass === 'AIR')
  const activeIds = new Set(
    options.activeRouteIds ?? activeRouteIdsFromMapWaves(definition.waves)
  )
  // Empty active set (degenerate) → treat all map routes as active.
  if (activeIds.size === 0) {
    for (const r of routes) activeIds.add(r.id)
  }

  const activeGround = groundRoutes.filter((r) => activeIds.has(r.id))
  const unusedGround = groundRoutes.filter((r) => !activeIds.has(r.id))
  const activeGroundCells = routeCellKeys(activeGround)
  const unusedOnlyCells = new Set<string>()
  for (const key of routeCellKeys(unusedGround)) {
    if (!activeGroundCells.has(key)) unusedOnlyCells.add(key)
  }
  // When no active ground routes resolved, all path tiles are "active".
  const hasActiveGround = activeGround.length > 0

  const isPath = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < grid.width && y < grid.height && grid.getTile(x, y) === 'path'

  // Soft board frame (environment, not debug rectangle)
  const origin = worldFromLogical(view, { x: 0, y: 0 })
  const mapW = grid.width * cell
  const mapH = grid.height * cell
  const pad = Math.max(6, cell * 0.28)
  edgeGfx.fillStyle(0x152033, 1)
  edgeGfx.fillRoundedRect(
    alignPx(origin.x - pad),
    alignPx(origin.y - pad),
    mapW + pad * 2,
    mapH + pad * 2,
    Math.max(6, cell * 0.22)
  )
  edgeGfx.lineStyle(strokeWidthForCell(cell, 'medium'), THEME.boardEdge, 0.55)
  edgeGfx.strokeRoundedRect(
    alignPx(origin.x - pad),
    alignPx(origin.y - pad),
    mapW + pad * 2,
    mapH + pad * 2,
    Math.max(6, cell * 0.22)
  )
  edgeGfx.lineStyle(strokeWidthForCell(cell, 'hair'), 0x0b1220, 0.45)
  edgeGfx.strokeRoundedRect(
    alignPx(origin.x - 1),
    alignPx(origin.y - 1),
    mapW + 2,
    mapH + 2,
    Math.max(2, cell * 0.06)
  )

  const motif = mapIdentityMotif(mapId)

  // Grass — macro patches, soft micro detail (not checkerboard)
  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      if (grid.getTile(x, y) === 'path') continue
      const topLeft = worldFromLogical(view, { x, y })
      const px = alignPx(topLeft.x)
      const py = alignPx(topLeft.y)
      const macro = grassMacroTone(mapId, x, y)
      const micro = grassMicroField(x, y)
      const base = placing ? GRASS_PLACE[macro] : GRASS_BASE[macro]
      graphics.fillStyle(base, 1)
      graphics.fillRect(px, py, cell, cell)

      // Soft blend toward neighbor patch (reduces hard cell edges)
      if (micro > 0.55) {
        graphics.fillStyle(GRASS_SOFT[macro], placing ? 0.18 + micro * 0.1 : 0.22 + micro * 0.12)
        graphics.fillRect(px, py, cell, cell)
      }

      // Buildability: slightly cooler inner wash while placing (still green grass)
      if (placing) {
        graphics.fillStyle(0x1a3320, 0.08)
        graphics.fillRect(px + inset, py + inset, cell - inset * 2, cell - inset * 2)
      }

      // Micro detail only when cells are large enough
      if (cell >= 22 && grassToneIndex(x, y) === 1) {
        graphics.fillStyle(0x2f5208, 0.28)
        const ox = px + cell * (0.25 + micro * 0.4)
        const oy = py + cell * (0.3 + (1 - micro) * 0.35)
        graphics.fillRect(alignPx(ox), alignPx(oy), Math.max(1, cell * 0.04), Math.max(2, cell * 0.1))
      } else if (cell >= 28 && grassToneIndex(x, y) === 2) {
        graphics.fillStyle(0x6b7280, 0.2)
        graphics.fillCircle(
          alignPx(px + cell * 0.65),
          alignPx(py + cell * 0.4),
          Math.max(1, cell * 0.035)
        )
      }

      paintMapIdentityFleck(graphics, motif, px, py, cell, x, y, micro)
    }
  }

  // Path — continuous surface; outer edges only (no per-cell boxed look).
  // Shared cells paint once via grid path tiles + neighbor joins.
  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      if (!isPath(x, y)) continue
      const topLeft = worldFromLogical(view, { x, y })
      const px = alignPx(topLeft.x)
      const py = alignPx(topLeft.y)
      const mask = pathNeighborMask(isPath, x, y)
      const n = neighborsFromMask(mask)
      const variation = grassMicroField(x + 3, y + 5)
      const key = cellKey(x, y)
      const subdued =
        hasActiveGround && unusedOnlyCells.has(key) && !activeGroundCells.has(key)
      const fill = subdued ? PATH_SUBDUED : THEME.path
      const edge = subdued ? PATH_SUBDUED_EDGE : THEME.pathEdge
      const fillAlpha = subdued ? 0.72 : 1

      graphics.fillStyle(fill, fillAlpha)
      graphics.fillRect(px + inset, py + inset, cell - inset * 2, cell - inset * 2)

      // Neighbor joins — continuous ribbon (no double borders on shared cells)
      if (n.n) graphics.fillRect(px + inset, py - pathPad, cell - inset * 2, pathPad + inset)
      if (n.s) graphics.fillRect(px + inset, py + cell - inset, cell - inset * 2, pathPad + inset)
      if (n.w) graphics.fillRect(px - pathPad, py + inset, pathPad + inset, cell - inset * 2)
      if (n.e) graphics.fillRect(px + cell - inset, py + inset, pathPad + inset, cell - inset * 2)

      // Soft internal tone (not a box outline)
      if (!subdued) {
        graphics.fillStyle(THEME.pathInner, 0.14 + variation * 0.08)
        graphics.fillRect(
          px + inset * 3,
          py + inset * 3,
          cell - inset * 6,
          Math.max(1, cell * 0.18)
        )
      }

      // Outer shoulder / depth where path meets grass (sits in terrain)
      const edgeW = Math.max(1, cell * 0.07)
      const shoulder = Math.max(1, cell * 0.04)
      graphics.fillStyle(edge, subdued ? 0.35 : 0.5)
      if (!n.n) {
        graphics.fillRect(px + inset - shoulder, py + inset - shoulder, cell - inset * 2 + shoulder * 2, edgeW)
      }
      if (!n.s) {
        graphics.fillRect(
          px + inset - shoulder,
          py + cell - inset - edgeW + shoulder,
          cell - inset * 2 + shoulder * 2,
          edgeW
        )
      }
      if (!n.w) {
        graphics.fillRect(px + inset - shoulder, py + inset - shoulder, edgeW, cell - inset * 2 + shoulder * 2)
      }
      if (!n.e) {
        graphics.fillRect(
          px + cell - inset - edgeW + shoulder,
          py + inset - shoulder,
          edgeW,
          cell - inset * 2 + shoulder * 2
        )
      }

      // Soft terrain bleed under outer shoulders (reads as path nestled in grass)
      if (!subdued) {
        graphics.fillStyle(0x2a4018, 0.18)
        const bleed = Math.max(1, cell * 0.035)
        if (!n.n) graphics.fillRect(px + inset, py + inset - bleed, cell - inset * 2, bleed)
        if (!n.s) graphics.fillRect(px + inset, py + cell - inset, cell - inset * 2, bleed)
        if (!n.w) graphics.fillRect(px + inset - bleed, py + inset, bleed, cell - inset * 2)
        if (!n.e) graphics.fillRect(px + cell - inset, py + inset, bleed, cell - inset * 2)
      }
    }
  }

  // Grid — clearer while placing; nearly invisible in play
  if (placing) {
    gridGfx.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.grid, 0.62)
    for (let x = 0; x <= grid.width; x += 1) {
      const a = worldFromLogical(view, { x, y: 0 })
      const b = worldFromLogical(view, { x, y: grid.height })
      gridGfx.lineBetween(alignPx(a.x), alignPx(a.y), alignPx(b.x), alignPx(b.y))
    }
    for (let y = 0; y <= grid.height; y += 1) {
      const a = worldFromLogical(view, { x: 0, y })
      const b = worldFromLogical(view, { x: grid.width, y })
      gridGfx.lineBetween(alignPx(a.x), alignPx(a.y), alignPx(b.x), alignPx(b.y))
    }
  } else if (cell >= 36) {
    gridGfx.lineStyle(strokeWidthForCell(cell, 'hair'), THEME.grid, 0.12)
    const tick = Math.max(2, cell * 0.12)
    for (let y = 0; y < grid.height; y += 2) {
      for (let x = 0; x < grid.width; x += 2) {
        const topLeft = worldFromLogical(view, { x, y })
        const px = alignPx(topLeft.x)
        const py = alignPx(topLeft.y)
        gridGfx.lineBetween(px, py, px + tick, py)
        gridGfx.lineBetween(px, py, px, py + tick)
      }
    }
  }

  paintRouteTerminals(markerGfx, view, groundRoutes, activeIds, cell)
  const airEmphasis =
    options.sessionState === 'PLAYING' ? 'combat' : options.sessionState === 'READY' || !options.sessionState ? 'ready' : 'quiet'
  for (const air of airRoutes) {
    const active = activeIds.has(air.id)
    paintAirRouteOverlay(markerGfx, view, air, cell, active, airEmphasis)
  }
}

/** Sparse geometric flecks — map identity without biome swaps. */
function paintMapIdentityFleck(
  g: Phaser.GameObjects.Graphics,
  motif: MapIdentityMotif,
  px: number,
  py: number,
  cell: number,
  x: number,
  y: number,
  micro: number
): void {
  if (cell < 24) return
  if (grassToneIndex(x, y) !== 0 || micro < 0.72) return
  const alpha = 0.12
  g.lineStyle(strokeWidthForCell(cell, 'hair'), 0x1e3a28, alpha)
  const cx = px + cell * 0.5
  const cy = py + cell * 0.5
  const s = cell * 0.12
  if (motif === 'ridge') {
    g.beginPath()
    g.moveTo(alignPx(cx - s), alignPx(cy + s * 0.2))
    g.lineTo(alignPx(cx), alignPx(cy - s * 0.35))
    g.lineTo(alignPx(cx + s), alignPx(cy + s * 0.2))
    g.strokePath()
  } else if (motif === 'dots') {
    g.fillStyle(0x2a4520, alpha)
    g.fillCircle(alignPx(cx), alignPx(cy), Math.max(1, cell * 0.03))
  } else {
    g.beginPath()
    g.arc(alignPx(cx), alignPx(cy), s * 0.7, 0.2, Math.PI * 0.9, false)
    g.strokePath()
  }
}

function paintRouteTerminals(
  g: Phaser.GameObjects.Graphics,
  view: GridView,
  groundRoutes: RouteDefinition[],
  activeIds: Set<string>,
  cell: number
): void {
  const multi = groundRoutes.filter((r) => activeIds.has(r.id)).length >= 2
  const paintedSpawn = new Set<string>()
  const paintedExit = new Set<string>()

  // Active first (emphasis), then unused (quiet)
  const ordered = [
    ...groundRoutes.filter((r) => activeIds.has(r.id)),
    ...groundRoutes.filter((r) => !activeIds.has(r.id))
  ]

  for (const route of ordered) {
    const active = activeIds.has(route.id)
    const sk = cellKey(route.spawn.x, route.spawn.y)
    const ek = cellKey(route.exit.x, route.exit.y)
    if (!paintedSpawn.has(sk)) {
      paintedSpawn.add(sk)
      paintSpawnMarker(g, view, route.spawn, cell, {
        routeId: route.id,
        active,
        showMark: multi || groundRoutes.length >= 2
      })
    }
    if (!paintedExit.has(ek)) {
      paintedExit.add(ek)
      paintExitMarker(g, view, route.exit, cell, {
        active,
        // Multiple distinct exits: keep clear but compact
        compact: paintedExit.size > 1 || groundRoutes.length >= 2
      })
    }
  }
}

/** AIR corridor — dashed trajectory + sparse chevrons, not a brown road. */
function paintAirRouteOverlay(
  g: Phaser.GameObjects.Graphics,
  view: GridView,
  air: RouteDefinition,
  cell: number,
  active: boolean,
  emphasis: 'ready' | 'combat' | 'quiet' = 'ready'
): void {
  if (air.cells.length < 2) return

  // READY: strong enough that humans read "different path" before START.
  // Combat: quieter so dense FX stays readable. Unused: whisper.
  let lineAlpha: number
  let nodeAlpha: number
  let chevronAlpha: number
  if (!active) {
    lineAlpha = 0.08
    nodeAlpha = 0.1
    chevronAlpha = 0.12
  } else if (emphasis === 'ready') {
    lineAlpha = 0.68
    nodeAlpha = 0.78
    chevronAlpha = 0.88
  } else if (emphasis === 'combat') {
    lineAlpha = 0.28
    nodeAlpha = 0.34
    chevronAlpha = 0.4
  } else {
    lineAlpha = 0.14
    nodeAlpha = 0.16
    chevronAlpha = 0.18
  }
  const lineW = Math.max(1.5, cell * (emphasis === 'ready' && active ? 0.055 : 0.032))
  const color = THEME.airRoute

  // Dashed segments between waypoints
  for (let i = 0; i < air.cells.length - 1; i += 1) {
    const a = air.cells[i]!
    const b = air.cells[i + 1]!
    const wa = worldFromLogical(view, { x: a.x + 0.5, y: a.y + 0.5 })
    const wb = worldFromLogical(view, { x: b.x + 0.5, y: b.y + 0.5 })
    const dx = wb.x - wa.x
    const dy = wb.y - wa.y
    const len = Math.hypot(dx, dy) || 1
    const ux = dx / len
    const uy = dy / len
    const dash = Math.max(3, cell * 0.2)
    const gap = Math.max(2, cell * 0.12)
    let dist = 0
    g.lineStyle(lineW, color, lineAlpha)
    while (dist < len) {
      const end = Math.min(dist + dash, len)
      g.beginPath()
      g.moveTo(alignPx(wa.x + ux * dist), alignPx(wa.y + uy * dist))
      g.lineTo(alignPx(wa.x + ux * end), alignPx(wa.y + uy * end))
      g.strokePath()
      dist = end + gap
    }

    // Sparse directional chevron mid-segment (every other segment)
    if (active && i % 2 === 0 && len > cell * 0.6) {
      const mx = wa.x + ux * (len * 0.5)
      const my = wa.y + uy * (len * 0.5)
      const cs = cell * (emphasis === 'ready' ? 0.11 : 0.08)
      const px = -uy
      const py = ux
      g.lineStyle(strokeWidthForCell(cell, 'thin'), color, chevronAlpha)
      g.beginPath()
      g.moveTo(alignPx(mx - ux * cs + px * cs * 0.7), alignPx(my - uy * cs + py * cs * 0.7))
      g.lineTo(alignPx(mx + ux * cs * 0.35), alignPx(my + uy * cs * 0.35))
      g.lineTo(alignPx(mx - ux * cs - px * cs * 0.7), alignPx(my - uy * cs - py * cs * 0.7))
      g.strokePath()
    }
  }

  // Waypoint nodes along corridor
  const nodeR = Math.max(1.4, cell * (emphasis === 'ready' && active ? 0.06 : 0.045))
  for (let i = 0; i < air.cells.length; i += 1) {
    if (i !== 0 && i !== air.cells.length - 1 && i % 2 === 1) continue
    const wp = air.cells[i]!
    const c = worldFromLogical(view, { x: wp.x + 0.5, y: wp.y + 0.5 })
    g.fillStyle(color, nodeAlpha * 0.65)
    g.fillCircle(alignPx(c.x), alignPx(c.y), nodeR)
    if (active && emphasis === 'ready') {
      g.lineStyle(strokeWidthForCell(cell, 'hair'), color, nodeAlpha)
      g.strokeCircle(alignPx(c.x), alignPx(c.y), nodeR * 1.6)
    }
  }

  const portalScale = active ? (emphasis === 'ready' ? 1 : 0.85) : 0.4
  paintAirPortalMarker(g, view, air.spawn, cell, true, portalScale)
  paintAirPortalMarker(g, view, air.exit, cell, false, portalScale)
}

/**
 * AIR spawn = diamond + wing chevron (≠ ground circle/square/hex).
 * AIR exit = open diamond ring (≠ ground diamond fill).
 */
function paintAirPortalMarker(
  g: Phaser.GameObjects.Graphics,
  view: GridView,
  cellPos: GridPosition,
  cell: number,
  isSpawn: boolean,
  alphaScale = 1
): void {
  const c = worldFromLogical(view, { x: cellPos.x + 0.5, y: cellPos.y + 0.5 })
  const cx = alignPx(c.x)
  const cy = alignPx(c.y)
  const r = cell * 0.28
  const color = isSpawn ? THEME.airRoute : THEME.exit

  // Outer diamond (shape distinction from ground portals)
  g.lineStyle(strokeWidthForCell(cell, 'medium'), color, 0.85 * alphaScale)
  g.beginPath()
  g.moveTo(cx, cy - r)
  g.lineTo(cx + r, cy)
  g.lineTo(cx, cy + r)
  g.lineTo(cx - r, cy)
  g.closePath()
  g.strokePath()

  if (isSpawn) {
    g.fillStyle(color, 0.18 * alphaScale)
    g.beginPath()
    g.moveTo(cx, cy - r * 0.55)
    g.lineTo(cx + r * 0.55, cy)
    g.lineTo(cx, cy + r * 0.55)
    g.lineTo(cx - r * 0.55, cy)
    g.closePath()
    g.fillPath()
    // Wing chevron — flight direction cue
    const s = cell * 0.1
    g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.white, 0.9 * alphaScale)
    g.beginPath()
    g.moveTo(cx - s * 1.1, cy + s * 0.15)
    g.lineTo(cx, cy - s * 0.7)
    g.lineTo(cx + s * 1.1, cy + s * 0.15)
    g.strokePath()
    g.beginPath()
    g.moveTo(cx - s * 0.75, cy + s * 0.55)
    g.lineTo(cx, cy - s * 0.15)
    g.lineTo(cx + s * 0.75, cy + s * 0.55)
    g.strokePath()
  } else {
    // Exit: hollow diamond + inner tick (landing)
    g.lineStyle(strokeWidthForCell(cell, 'thin'), color, 0.7 * alphaScale)
    g.beginPath()
    g.moveTo(cx, cy - r * 0.5)
    g.lineTo(cx + r * 0.5, cy)
    g.lineTo(cx, cy + r * 0.5)
    g.lineTo(cx - r * 0.5, cy)
    g.closePath()
    g.strokePath()
    g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.white, 0.75 * alphaScale)
    g.lineBetween(cx, cy - r * 0.22, cx, cy + r * 0.22)
    g.lineBetween(cx - r * 0.18, cy, cx + r * 0.18, cy)
  }
}

interface SpawnPaintOpts {
  routeId: string
  active: boolean
  showMark: boolean
}

function paintSpawnMarker(
  g: Phaser.GameObjects.Graphics,
  view: GridView,
  spawn: GridPosition,
  cell: number,
  opts: SpawnPaintOpts
): void {
  const c = worldFromLogical(view, { x: spawn.x + 0.5, y: spawn.y + 0.5 })
  const cx = alignPx(c.x)
  const cy = alignPx(c.y)
  const r = cell * (opts.active ? 0.36 : 0.28)
  const alpha = opts.active ? 1 : 0.38
  const shape = spawnLaneShape(opts.routeId)
  const mark = routeLaneMark(opts.routeId)

  g.fillStyle(THEME.pathEdge, 0.5 * alpha)
  paintLaneSilhouette(g, shape, cx, cy, r * 1.08, true)

  g.lineStyle(strokeWidthForCell(cell, 'medium'), THEME.spawn, 0.95 * alpha)
  paintLaneSilhouette(g, shape, cx, cy, r, false)

  g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.selection, 0.8 * alpha)
  paintLaneSilhouette(g, shape, cx, cy, r * 0.58, false)

  g.fillStyle(THEME.spawn, 0.16 * alpha)
  paintLaneSilhouette(g, shape, cx, cy, r * 0.32, true)

  // Compact lane mark (matches intel A/B/C) — geometry strokes, not text objects
  if (opts.showMark && mark !== 'AIR') {
    paintCompactLaneGlyph(g, mark, cx, cy, cell, alpha)
  } else if (!opts.showMark) {
    // Single-lane: dual chevrons (portal feel)
    g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.white, 0.9 * alpha)
    const s = cell * 0.12
    for (const dy of [-s * 0.35, s * 0.15]) {
      g.beginPath()
      g.moveTo(cx - s, cy + dy)
      g.lineTo(cx, cy + dy + s * 0.55)
      g.lineTo(cx + s, cy + dy)
      g.strokePath()
    }
  }
}

function paintLaneSilhouette(
  g: Phaser.GameObjects.Graphics,
  shape: SpawnLaneShape,
  cx: number,
  cy: number,
  r: number,
  fill: boolean
): void {
  if (shape === 'circle') {
    if (fill) g.fillCircle(cx, cy, r)
    else g.strokeCircle(cx, cy, r)
    return
  }
  if (shape === 'square') {
    const s = r * 1.55
    if (fill) g.fillRoundedRect(cx - s / 2, cy - s / 2, s, s, Math.max(2, r * 0.22))
    else g.strokeRoundedRect(cx - s / 2, cy - s / 2, s, s, Math.max(2, r * 0.22))
    return
  }
  // Hex (flat-top)
  const pts = hexPoints(cx, cy, r)
  g.beginPath()
  g.moveTo(pts[0]!.x, pts[0]!.y)
  for (let i = 1; i < pts.length; i += 1) g.lineTo(pts[i]!.x, pts[i]!.y)
  g.closePath()
  if (fill) g.fillPath()
  else g.strokePath()
}

function hexPoints(cx: number, cy: number, r: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = []
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 180) * (60 * i - 30)
    out.push({ x: alignPx(cx + r * Math.cos(a)), y: alignPx(cy + r * Math.sin(a)) })
  }
  return out
}

/** Tiny stroke glyphs for A / B / C — readable at ~20px cells. */
function paintCompactLaneGlyph(
  g: Phaser.GameObjects.Graphics,
  mark: string,
  cx: number,
  cy: number,
  cell: number,
  alpha: number
): void {
  const s = cell * 0.11
  g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.white, 0.95 * alpha)
  if (mark === 'A') {
    g.beginPath()
    g.moveTo(cx - s, cy + s * 0.85)
    g.lineTo(cx, cy - s)
    g.lineTo(cx + s, cy + s * 0.85)
    g.strokePath()
    g.lineBetween(cx - s * 0.45, cy + s * 0.2, cx + s * 0.45, cy + s * 0.2)
  } else if (mark === 'B') {
    g.strokeRect(cx - s * 0.55, cy - s, s * 0.55, s * 2)
    g.beginPath()
    g.arc(cx, cy - s * 0.45, s * 0.55, -Math.PI / 2, Math.PI / 2, false)
    g.strokePath()
    g.beginPath()
    g.arc(cx, cy + s * 0.45, s * 0.65, -Math.PI / 2, Math.PI / 2, false)
    g.strokePath()
  } else {
    // C
    g.beginPath()
    g.arc(cx + s * 0.15, cy, s * 0.95, Math.PI * 0.25, Math.PI * 1.75, false)
    g.strokePath()
  }
}

interface ExitPaintOpts {
  active: boolean
  compact: boolean
}

function paintExitMarker(
  g: Phaser.GameObjects.Graphics,
  view: GridView,
  exit: GridPosition,
  cell: number,
  opts: ExitPaintOpts
): void {
  const c = worldFromLogical(view, { x: exit.x + 0.5, y: exit.y + 0.5 })
  const cx = alignPx(c.x)
  const cy = alignPx(c.y)
  const scale = opts.compact ? 0.78 : 1
  const alpha = opts.active ? 1 : 0.35
  const s = cell * 0.34 * scale

  g.fillStyle(THEME.pathEdge, 0.4 * alpha)
  g.beginPath()
  g.moveTo(cx, cy - s * 1.08)
  g.lineTo(cx + s * 1.08, cy)
  g.lineTo(cx, cy + s * 1.08)
  g.lineTo(cx - s * 1.08, cy)
  g.closePath()
  g.fillPath()

  g.lineStyle(strokeWidthForCell(cell, opts.compact ? 'thin' : 'medium'), THEME.exit, 0.95 * alpha)
  g.beginPath()
  g.moveTo(cx, cy - s)
  g.lineTo(cx + s, cy)
  g.lineTo(cx, cy + s)
  g.lineTo(cx - s, cy)
  g.closePath()
  g.strokePath()
  g.fillStyle(THEME.exit, 0.24 * alpha)
  g.fillPath()

  const s2 = s * 0.4
  g.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.selection, 0.85 * alpha)
  g.beginPath()
  g.moveTo(cx, cy - s2)
  g.lineTo(cx + s2, cy)
  g.lineTo(cx, cy + s2)
  g.lineTo(cx - s2, cy)
  g.closePath()
  g.strokePath()
  g.fillStyle(THEME.selection, 0.32 * alpha)
  g.fillCircle(cx, cy, Math.max(1.2, cell * 0.06 * scale))
}
